import addDays from 'date-fns/add_days'
import differenceInCalendarDays from 'date-fns/difference_in_calendar_days'
import parseDate from 'date-fns/parse'
import formatDate from '@utils/format-date'

function normalizeDateStr(dateStr) {
  if (!dateStr) return ''
  return String(dateStr).slice(0, 10)
}

/**
 * 缺省視為 1（包含退房日），維持既有畫面行為。
 * @param {unknown} value
 * @returns {0|1}
 */
export function normalizeShowCheckoutDate(value) {
  if (value === 0 || value === '0' || value === false) return 0
  if (value == null || value === '') return 1
  return Number(value) === 1 ? 1 : 0
}

/**
 * API 日期為實際住宿日。
 * showCheckoutDate=1：顯示退房日 = 最後住宿日 + 1
 * showCheckoutDate=0：顯示退房日 = 最後住宿日
 */
function toDisplayCheckoutDate(lastStayDate, showCheckoutDate = 1) {
  const normalized = normalizeDateStr(lastStayDate)
  if (!normalized) return ''
  const includeCheckout = normalizeShowCheckoutDate(showCheckoutDate) === 1
  if (!includeCheckout) return formatDate(parseDate(normalized), 'date-dash')
  return formatDate(addDays(parseDate(normalized), 1), 'date-dash')
}

function extractBookingDates(bookings) {
  if (!bookings || bookings.length === 0) return []
  return bookings
    .map((booking) => normalizeDateStr(booking?.date ?? booking))
    .filter(Boolean)
    .sort()
}

function resolveNights(checkIn, lastStayNight, nights) {
  if (nights != null && Number(nights) > 0) return Number(nights)
  if (!checkIn || !lastStayNight) return 0
  return differenceInCalendarDays(parseDate(lastStayNight), parseDate(checkIn)) + 1
}

/**
 * @param {number|string} nights
 * @param {unknown} showCheckoutDate
 * @returns {string} e.g. "4晚" / "4天"
 */
export function formatStayNightLabel(nights, showCheckoutDate = 1) {
  const n = Number(nights)
  const count = Number.isFinite(n) && n > 0 ? n : 0
  const unit = normalizeShowCheckoutDate(showCheckoutDate) === 1 ? '晚' : '天'
  return `${count}${unit}`
}

/**
 * API 的 checkIn / checkout / bookings[].date 皆為實際住宿日；
 * 回傳的 checkout 為畫面顯示用退房日（依 showCheckoutDate 決定是否 +1）。
 *
 * @param {{ checkInDate?: string, checkoutDate?: string, bookings?: Array<{ date?: string }|string>, nights?: number, showCheckoutDate?: 0|1|boolean|string }} input
 * @returns {{ checkIn: string, checkout: string, nights: number } | null}
 */
export function resolveHotelStay({ checkInDate, checkoutDate, bookings, nights, showCheckoutDate = 1 } = {}) {
  const flag = normalizeShowCheckoutDate(showCheckoutDate)
  const bookingDates = extractBookingDates(bookings)

  if (bookingDates.length > 0) {
    const lastStayNight = bookingDates[bookingDates.length - 1]
    return {
      checkIn: bookingDates[0],
      checkout: toDisplayCheckoutDate(lastStayNight, flag),
      nights: bookingDates.length,
    }
  }

  if (checkInDate && checkoutDate) {
    const checkIn = normalizeDateStr(checkInDate)
    const lastStayNight = normalizeDateStr(checkoutDate)
    const resolvedNights = resolveNights(checkIn, lastStayNight, nights)

    if (!checkIn || !lastStayNight || resolvedNights < 1) return null

    return {
      checkIn,
      checkout: toDisplayCheckoutDate(lastStayNight, flag),
      nights: resolvedNights,
    }
  }

  return null
}

/**
 * 從 hotel 訂單物件取出住宿日並轉為顯示用區間
 * @param {object} order
 * @param {unknown} [showCheckoutDate]
 * @returns {{ checkIn: string, checkout: string, nights: number } | null}
 */
export function resolveHotelOrderStay(order = {}, showCheckoutDate = 1) {
  const bookings = Array.isArray(order.bookings) ? order.bookings : []
  return resolveHotelStay({
    checkInDate: order.checkinDate || order.startDate,
    checkoutDate: order.checkoutDate || order.endDate,
    bookings,
    nights: order.nights,
    showCheckoutDate,
  })
}

/**
 * @param {object} order
 * @param {unknown} [showCheckoutDate]
 * @returns {string}
 */
export function formatHotelOrderStayRange(order = {}, showCheckoutDate = 1) {
  const bookings = Array.isArray(order.bookings) ? order.bookings : []
  return formatHotelStayRange({
    checkInDate: order.checkinDate || order.startDate,
    checkoutDate: order.checkoutDate || order.endDate,
    bookings,
    nights: order.nights,
    showCheckoutDate,
  })
}

/**
 * @param {{ checkInDate?: string, checkoutDate?: string, bookings?: Array<{ date?: string }|string>, nights?: number, showCheckoutDate?: 0|1|boolean|string }} input
 * @returns {string}
 */
export function formatHotelStayRange(input = {}) {
  const showCheckoutDate = normalizeShowCheckoutDate(input.showCheckoutDate)
  const stay = resolveHotelStay({ ...input, showCheckoutDate })
  if (!stay) return ''

  const fmt = (dateStr) => formatDate(dateStr, 'date-dash')
  const nightLabel = formatStayNightLabel(stay.nights, showCheckoutDate)

  if (showCheckoutDate === 1) {
    return `${fmt(stay.checkIn)} - ${fmt(stay.checkout)} (退房) 合計${nightLabel}`
  }
  return `${fmt(stay.checkIn)} - ${fmt(stay.checkout)} 合計${nightLabel}`
}

/**
 * 將 API 最後住宿日轉為畫面顯示用退房日
 * @param {string} lastStayDate
 * @param {unknown} [showCheckoutDate]
 * @returns {string}
 */
export function formatHotelCheckoutDisplay(lastStayDate, showCheckoutDate = 1) {
  return toDisplayCheckoutDate(lastStayDate, showCheckoutDate)
}
